/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import FocusRing from '@atlaskit/focus-ring';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import { token } from '@atlaskit/tokens';

import CopyToClipboard from './copy-to-clipboard';

const copyValueButtonStyles = css({
  display: 'inline-flex',
  padding: `${token('space.025', '2px')} ${token('space.0', '0px')}`,
  alignItems: 'center',
  appearance: 'none',
  background: 'none',
  border: 'none',
  borderRadius: token('border.radius.100', '3px'),
  color: 'inherit',
  cursor: 'pointer',
  '&:hover, &:focus': {
    textDecoration: 'underline',
    '.copy-button-icon': {
      opacity: 1,
    },
  },
});

const copyValueIconStyles = css({
  opacity: 0,
});

interface CopyButtonProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * __Copy button__
 *
 * A clickable icon that copies a string to the user's clipboard.
 *
 */
const CopyButton = ({ value, children, className }: CopyButtonProps) => (
  <CopyToClipboard
    value={value}
    messages={{
      prompt: `Copy '${value}' to clipboard`,
      success: 'Copied!',
      fail: 'Copy failed',
    }}
  >
    {({ copy }) => (
      <FocusRing>
        <button
          css={copyValueButtonStyles}
          className={className}
          type="button"
          onClick={copy}
          onMouseDown={(event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault();
          }}
        >
          {children}
          <span css={copyValueIconStyles} className="copy-button-icon">
            <CopyIcon size="small" label="" />
          </span>
        </button>
      </FocusRing>
    )}
  </CopyToClipboard>
);

export default CopyButton;
